import { supabase } from '@/lib/supabase';
import type {
  AIFlag,
  BloodType,
  EventType,
  Gender,
  Relation,
  ShareLink,
} from '@/types/db';

export type ShareErrorCode =
  | 'unauthorized'
  | 'forbidden'
  | 'profile_not_found'
  | 'no_records'
  | 'share_creation_failed'
  | 'summary_failed'
  | 'network_error';

export type ExpiryDays = 1 | 3 | 7;

export interface GenerateShareResult {
  success: true;
  token: string;
  url: string;
}

export type GenerateShareOutcome =
  | GenerateShareResult
  | { success: false; error: ShareErrorCode };

const KNOWN_ERRORS: ReadonlySet<ShareErrorCode> = new Set<ShareErrorCode>([
  'unauthorized',
  'forbidden',
  'profile_not_found',
  'no_records',
  'share_creation_failed',
  'summary_failed',
  'network_error',
]);

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null;
}

function toErrorCode(v: unknown): ShareErrorCode {
  return typeof v === 'string' && KNOWN_ERRORS.has(v as ShareErrorCode)
    ? (v as ShareErrorCode)
    : 'summary_failed';
}

export async function generateShareLink(
  profileId: string,
  options: { expiresInDays?: ExpiryDays; password?: string } = {},
): Promise<GenerateShareOutcome> {
  try {
    const { data, error } = await supabase.functions.invoke('generate-summary', {
      body: {
        profile_id: profileId,
        base_url: window.location.origin,
        expires_in_days: options.expiresInDays ?? 7,
        ...(options.password ? { password: options.password } : {}),
      },
    });
    if (error) {
      const code = await extractFunctionErrorCode(error);
      if (code && KNOWN_ERRORS.has(code as ShareErrorCode)) {
        return { success: false, error: code as ShareErrorCode };
      }
      console.error('[share] generate invoke error', error);
      return { success: false, error: 'network_error' };
    }
    if (!isRecord(data)) {
      return { success: false, error: 'summary_failed' };
    }
    if (
      data.success === true &&
      typeof data.token === 'string' &&
      typeof data.url === 'string'
    ) {
      return { success: true, token: data.token, url: data.url };
    }
    return { success: false, error: toErrorCode(data.error) };
  } catch (err) {
    console.error('[share] generate unexpected', err);
    return { success: false, error: 'network_error' };
  }
}

export function shareErrorMessage(code: ShareErrorCode): string {
  switch (code) {
    case 'unauthorized':
      return 'Please sign in again.';
    case 'forbidden':
      return 'Only the profile owner can create a share link.';
    case 'profile_not_found':
      return "We couldn't find that profile.";
    case 'no_records':
      return 'Add at least one record before sharing with a doctor.';
    case 'network_error':
      return "We couldn't reach the server. Check your connection.";
    case 'share_creation_failed':
      return "We couldn't save the share link. Please try again.";
    default:
      return "We couldn't create the link. Please try again.";
  }
}

/* ----- Public share view ---------------------------------------------- */

export interface PublicShareMedication {
  drug_name: string | null;
  dosage: string | null;
  frequency: string | null;
  duration_days: number | null;
  start_date: string | null;
  end_date: string | null;
}

export interface PublicShareAttachment {
  storage_path: string;
  file_type: string | null;
  signed_url: string | null;
}

export interface PublicShareEvent {
  id: string;
  event_type: EventType;
  title: string | null;
  event_date: string | null;
  doctor_name: string | null;
  clinic_name: string | null;
  diagnosis: string | null;
  notes: string | null;
  ai_flags: AIFlag[];
  medications: PublicShareMedication[];
  attachments: PublicShareAttachment[];
}

export interface PublicShareProfile {
  id: string;
  name: string;
  relation: Relation | null;
  date_of_birth: string | null;
  allergies: string[];
  blood_type: BloodType | null;
  gender: Gender | null;
  height_cm: number | null;
  weight_kg: number | null;
}

export interface PublicShareData {
  profile: PublicShareProfile;
  summary: string | null;
  summary_created_at: string | null;
  expires_at: string;
  events: PublicShareEvent[];
}

export type LoadShareOutcome =
  | { status: 'ready'; data: PublicShareData }
  | { status: 'expired' }
  | { status: 'not_found' }
  | { status: 'password_required' }
  | { status: 'wrong_password' }
  | { status: 'error' };

async function extractFunctionStatus(err: unknown): Promise<number | null> {
  if (!isRecord(err)) return null;
  const ctx = err.context;
  if (ctx instanceof Response) return ctx.status;
  if (isRecord(ctx) && typeof ctx.status === 'number') return ctx.status;
  return null;
}

async function extractFunctionErrorCode(err: unknown): Promise<string | null> {
  if (!isRecord(err)) return null;
  const ctx = err.context;
  try {
    if (ctx instanceof Response) {
      const clone = ctx.clone();
      const body = await clone.json();
      if (isRecord(body) && typeof body.error === 'string') return body.error;
    }
  } catch {
    /* body not JSON — ignore */
  }
  return null;
}

export async function loadSharedProfile(
  token: string,
  password?: string,
): Promise<LoadShareOutcome> {
  try {
    // Callers of this function may have no Supabase session (e.g. a doctor
    // opening a share link for the first time). During the auth init window
    // the client's Authorization header can be empty, causing a 401 at the
    // relay before the function even runs. Pinning to the anon key here
    // guarantees a valid JWT is always sent for this public endpoint.
    const { data, error } = await supabase.functions.invoke('public-share', {
      body: { token, ...(password ? { password } : {}) },
      headers: {
        Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
      },
    });
    if (error) {
      const status = await extractFunctionStatus(error);
      const errCode = await extractFunctionErrorCode(error);
      console.error('[share] public-share http_status=' + status + ' err_code=' + errCode, error);
      if (status === 410) return { status: 'expired' };
      if (status === 404) return { status: 'not_found' };
      if (status === 401) {
        if (errCode === 'wrong_password') return { status: 'wrong_password' };
        return { status: 'password_required' };
      }
      return { status: 'error' };
    }
    if (!isRecord(data) || data.success !== true || !isRecord(data.data)) {
      console.error('[share] public-share bad_shape data=', data);
      return { status: 'error' };
    }
    return { status: 'ready', data: data.data as unknown as PublicShareData };
  } catch (err) {
    console.error('[share] public-share unexpected', err);
    return { status: 'error' };
  }
}

/* ----- Owner-side share link management ------------------------------- */

export interface CachedSummaryMeta {
  summary: string;
  createdAt: string;
}

export async function getLatestCachedSummary(
  profileId: string,
): Promise<CachedSummaryMeta | null> {
  const { data, error } = await supabase
    .from('share_links')
    .select('cached_summary, created_at')
    .eq('profile_id', profileId)
    .not('cached_summary', 'is', null)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  const row = data as { cached_summary: string; created_at: string } | null;
  if (!row?.cached_summary) return null;
  return { summary: row.cached_summary, createdAt: row.created_at };
}

export async function listShareLinks(profileId: string): Promise<ShareLink[]> {
  const { data, error } = await supabase
    .from('share_links')
    .select('*')
    .eq('profile_id', profileId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data ?? []) as ShareLink[];
}

export async function revokeShareLink(linkId: string): Promise<void> {
  const { data, error } = await supabase
    .from('share_links')
    .update({ revoked_at: new Date().toISOString() })
    .eq('id', linkId)
    .select('id');
  if (error) throw error;
  if (!data || data.length === 0) {
    throw new Error('revoke_no_match');
  }
}
